import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  FormControl, InputLabel, Select, Switch, FormControlLabel, Box, Typography,
  IconButton, Tooltip, Checkbox, ListSubheader,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TargetPickerDialog from './TargetPickerDialog';
import SourcePickerDialog from './SourcePickerDialog';
import {
  MATCH_MODES, OPERATORS, operatorInput, NUMBER_OPERATORS, deptLabel, sourceLabel,
  EXCLUDED_DEPARTMENT_TYPES, DISPATCH_TYPES, SELECTABLE_DISPATCH_TYPES, DISPATCH_TYPE_DEFAULT,
  SETTING_TOKEN_PREFIX, isSettingToken, settingTokenKey,
} from './dispatchConstants';

const emptyRule = () => ({
  key: Math.random().toString(36).slice(2), id: null, source: 'feed.status', operator: 'eq', value: '',
});

const DispatchEditorModal = ({
  open, onClose, dispatchItem, dealership, departments, settingOptions, dynamicItems, nextOrder, onSave, onDispatchTypeChange,
}) => {
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [dispatchType, setDispatchType] = useState(DISPATCH_TYPE_DEFAULT);
  const [matchMode, setMatchMode] = useState('all');
  const [priority, setPriority] = useState(0);
  const [note, setNote] = useState('');
  const [useSetting, setUseSetting] = useState(false);
  const [targetDeptId, setTargetDeptId] = useState('');
  const [targetSetting, setTargetSetting] = useState('');
  const [rules, setRules] = useState([emptyRule()]);
  const [removedRuleIds, setRemovedRuleIds] = useState([]);
  const [targetPickerOpen, setTargetPickerOpen] = useState(false);
  const [sourcePickerRule, setSourcePickerRule] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (dispatchItem) {
      setName(dispatchItem.name || '');
      setEnabled(dispatchItem.enabled !== false);
      setDispatchType(dispatchItem.dispatch_type || DISPATCH_TYPE_DEFAULT);
      setMatchMode(dispatchItem.match_mode || 'all');
      setPriority(Number(dispatchItem.priority) || 0);
      setNote(dispatchItem.note || '');
      setUseSetting(!!dispatchItem.target_setting);
      setTargetDeptId(dispatchItem.target_department_id != null ? String(dispatchItem.target_department_id) : '');
      setTargetSetting(dispatchItem.target_setting || '');
      const existing = (dispatchItem.feed_dispatch_rules || [])
        .slice()
        .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))
        .map(r => ({
          key: `r${r.id}`, id: r.id, source: r.source || 'feed.status', operator: r.operator || 'eq', value: r.value || '',
        }));
      setRules(existing.length ? existing : [emptyRule()]);
    } else {
      setName('');
      setEnabled(false);
      setDispatchType(DISPATCH_TYPE_DEFAULT);
      setMatchMode('all');
      setPriority(Number(nextOrder) || 1);
      setNote('');
      setUseSetting(false);
      setTargetDeptId('');
      setTargetSetting('');
      setRules([emptyRule()]);
    }
    setRemovedRuleIds([]);
  }, [open, dispatchItem]);

  const allDeptOptions = useMemo(() => departments
    .filter(d => !EXCLUDED_DEPARTMENT_TYPES.includes(String(d.department_type || '').toLowerCase()))
    .map(d => ({
      id: d.id != null ? d.id : d.department_id,
      name: d.name,
      name_alias: d.name_alias || '',
      dealerships: String(d.dealerships || '').split(',').map(s => s.trim()).filter(Boolean),
    }))
    .filter(d => d.id != null && d.name)
    .sort((a, b) => String(a.name).localeCompare(String(b.name))), [departments]);

  const locationDeptOptions = useMemo(
    () => allDeptOptions.filter(d => !dealership || d.dealerships.includes(dealership)),
    [allDeptOptions, dealership],
  );

  const targetSummary = useMemo(() => {
    if (useSetting) return targetSetting ? `Setting: ${targetSetting}` : 'No setting chosen';
    if (targetDeptId !== '') {
      const d = allDeptOptions.find(o => String(o.id) === String(targetDeptId));
      return `Department: ${d ? deptLabel(d) : `#${targetDeptId}`}`;
    }
    return 'No target chosen';
  }, [useSetting, targetSetting, targetDeptId, allDeptOptions]);

  const onPickTarget = (sel) => {
    if (sel.type === 'department') {
      setUseSetting(false);
      setTargetDeptId(String(sel.id));
    } else {
      setUseSetting(true);
      setTargetSetting(sel.key);
    }
    setTargetPickerOpen(false);
  };

  const setRule = (key, patch) => setRules(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const addRule = () => setRules(prev => [...prev, emptyRule()]);
  const removeRule = (key) => setRules((prev) => {
    const row = prev.find(r => r.key === key);
    if (row && row.id) setRemovedRuleIds(ids => [...ids, row.id]);
    const next = prev.filter(r => r.key !== key);
    return next.length ? next : [emptyRule()];
  });

  const valid = name.trim() && (useSetting ? targetSetting.trim() : targetDeptId !== '') && rules.every(r => r.source && r.operator);

  const handleSave = () => {
    if (!valid) return;
    const rulesAttrs = rules.map((r, i) => {
      const attr = {
        source: r.source, operator: r.operator, value: operatorInput(r.operator) === 'none' ? '' : (r.value || ''), position: i,
      };
      if (r.id) attr.id = r.id;
      if (NUMBER_OPERATORS.includes(r.operator)) attr.value_type = 'number';
      return attr;
    });
    removedRuleIds.forEach(id => rulesAttrs.push({ id, _destroy: true }));
    const payload = {
      name: name.trim(),
      dealership: dealership || '',
      dispatch_type: dispatchType,
      priority: Number(priority) || 0,
      enabled,
      match_mode: matchMode,
      note: note.trim() ? note.trim() : null,
      target_department_id: useSetting ? null : Number(targetDeptId),
      target_setting: useSetting ? targetSetting.trim() : null,
      feed_dispatch_rules_attributes: rulesAttrs,
    };
    onSave(payload, dispatchItem && dispatchItem.id);
  };

  // "In Transit (setting: used_car_default_department)" — the dept the setting
  // points at right now, for context; the saved value stays the token.
  const settingTokenLabel = (token) => {
    const key = settingTokenKey(token);
    const opt = (settingOptions || []).find(o => o.key === key);
    const d = opt && allDeptOptions.find(o => String(o.id) === String(opt.value));
    return d ? `${deptLabel(d)} (setting: ${key})` : `setting: ${key}`;
  };

  const renderValueInput = (r) => {
    const input = operatorInput(r.operator);
    if (input === 'none') {
      return <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'center' }}>(no value)</Typography>;
    }
    const isDeptId = r.source === 'vehicle.department_id';
    if (input === 'in_department' || (isDeptId && input === 'list')) {
      const selected = (r.value || '').split(',').map(v => v.trim()).filter(Boolean);
      // Setting tokens: every dept-ish setting, plus any token already saved on
      // the rule (so it can still be unchecked even if the store lacks it).
      const tokens = (settingOptions || []).map(o => `${SETTING_TOKEN_PREFIX}${o.key}`);
      selected.filter(v => isSettingToken(v) && !tokens.includes(v)).forEach(v => tokens.push(v));
      return (
        <FormControl size="small" fullWidth>
          <Select
            multiple
            displayEmpty
            value={selected}
            onChange={e => setRule(r.key, { value: (Array.isArray(e.target.value) ? e.target.value : []).join(',') })}
            renderValue={vals => (vals.length ? vals.map((id) => {
              if (isSettingToken(id)) return settingTokenLabel(id);
              const d = allDeptOptions.find(o => String(o.id) === String(id));
              return d ? deptLabel(d) : `#${id}`;
            }).join(', ') : 'Select departments')}
          >
            {locationDeptOptions.map(d => (
              <MenuItem key={d.id} value={String(d.id)}>
                <Checkbox checked={selected.includes(String(d.id))} />
                {deptLabel(d)}
              </MenuItem>
            ))}
            {tokens.length > 0 && <ListSubheader>Dealer settings (resolved per store)</ListSubheader>}
            {tokens.map(t => (
              <MenuItem key={t} value={t}>
                <Checkbox checked={selected.includes(t)} />
                {settingTokenLabel(t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (isDeptId) {
      return (
        <FormControl size="small" fullWidth>
          <Select
            displayEmpty
            value={r.value || ''}
            onChange={e => setRule(r.key, { value: e.target.value })}
            renderValue={(val) => {
              if (!val) return 'Select department';
              const d = locationDeptOptions.find(o => String(o.id) === String(val));
              return d ? deptLabel(d) : `#${val}`;
            }}
          >
            {locationDeptOptions.map(d => (
              <MenuItem key={d.id} value={String(d.id)}>{deptLabel(d)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    let placeholder = 'value';
    if (input === 'list') placeholder = 'comma-separated (e.g. U,W)';
    else if (input === 'regex') placeholder = 'regex';
    return (
      <TextField
        size="small"
        fullWidth
        type={input === 'number' ? 'number' : 'text'}
        placeholder={placeholder}
        value={r.value}
        onChange={e => setRule(r.key, { value: e.target.value })}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {dispatchItem ? 'Edit dispatch' : 'New dispatch'}
        {dealership ? ` — ${dealership}` : ' — group default'}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Name" size="small" fullWidth required value={name} onChange={e => setName(e.target.value)} />
          <FormControl size="small" fullWidth>
            <InputLabel id="feed-dispatch-editor-type-label">Dispatch type</InputLabel>
            <Select
              labelId="feed-dispatch-editor-type-label"
              label="Dispatch type"
              value={dispatchType}
              onChange={(e) => {
                setDispatchType(e.target.value);
                // New dispatch: switch the list behind the modal to this type
                if (!dispatchItem && onDispatchTypeChange) onDispatchTypeChange(e.target.value);
              }}
            >
              {SELECTABLE_DISPATCH_TYPES.concat(DISPATCH_TYPES.filter(t => t.hidden && t.value === dispatchType))
                .map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              {(DISPATCH_TYPES.find(t => t.value === dispatchType) || {}).description || ''}
            </Typography>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField label="Priority order" size="small" type="number" sx={{ width: 140 }} value={priority} onChange={e => setPriority(e.target.value)} helperText="1 runs first" />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="feed-dispatch-editor-match-label">Match mode</InputLabel>
              <Select labelId="feed-dispatch-editor-match-label" label="Match mode" value={matchMode} onChange={e => setMatchMode(e.target.value)}>
                {MATCH_MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />} label="Enabled" />
          </Box>

          <Box sx={{
            display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between',
          }}
          >
            <Box>
              <Typography variant="caption" color="textSecondary">Moves matching vehicles to</Typography>
              <Typography variant="body2">{targetSummary}</Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={() => setTargetPickerOpen(true)}>Choose target</Button>
          </Box>

          <TextField label="Vehicle note (optional)" size="small" fullWidth value={note} onChange={e => setNote(e.target.value)} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Rules
              {matchMode === 'all' ? ' (all must match)' : ' (any may match)'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {rules.map(r => (
                <Box key={r.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSourcePickerRule(r.key)}
                    sx={{
                      minWidth: 200, height: 40, justifyContent: 'flex-start', textTransform: 'none',
                    }}
                  >
                    {sourceLabel(r.source)}
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select value={r.operator} onChange={e => setRule(r.key, { operator: e.target.value })}>
                      {OPERATORS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Box sx={{ flex: 1, minWidth: 160 }}>{renderValueInput(r)}</Box>
                  <Tooltip title="Remove rule"><IconButton size="small" onClick={() => removeRule(r.key)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              ))}
            </Box>
            <Button size="small" startIcon={<AddIcon />} onClick={addRule} sx={{ mt: 1 }}>Add rule</Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valid}>Save dispatch</Button>
      </DialogActions>
      <TargetPickerDialog
        open={targetPickerOpen}
        onClose={() => setTargetPickerOpen(false)}
        departments={allDeptOptions}
        scope={dealership}
        settingOptions={settingOptions || []}
        initialTab={useSetting ? 'setting' : 'department'}
        onSelect={onPickTarget}
      />
      <SourcePickerDialog
        open={sourcePickerRule != null}
        value={(rules.find(r => r.key === sourcePickerRule) || {}).source || ''}
        dynamicItems={dynamicItems}
        onClose={() => setSourcePickerRule(null)}
        onSelect={(src) => { setRule(sourcePickerRule, { source: src }); setSourcePickerRule(null); }}
      />
    </Dialog>
  );
};

export default DispatchEditorModal;
